class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
        document.dispatchEvent(new CustomEvent('facets:updated'));
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
    document.dispatchEvent(new CustomEvent('facets:updated'));
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
    syncOutOfRangeCategoryPills();
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const targetForm = event.target.closest('form');
      this.preserveOutOfRangeCategoryFiltersIfApplicable(targetForm);
      const searchParams = this.createSearchParams(targetForm);
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            this.preserveOutOfRangeCategoryFiltersIfApplicable(form);
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          this.preserveOutOfRangeCategoryFiltersIfApplicable(form);
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  /**
   * カテゴリ UI を持つフォーム（FacetFiltersForm / FacetFiltersFormMobile）に対してのみ
   * 100 件外カテゴリーフィルターの補完を実行する。それ以外のフォーム（ソート用など）は
   * filter.p.m.custom.category 系 input を持たないため、補完対象から除外しないと
   * URL 上で値が重複する。
   */
  preserveOutOfRangeCategoryFiltersIfApplicable(form) {
    if (!form) return;
    if (form.id !== 'FacetFiltersForm' && form.id !== 'FacetFiltersFormMobile') return;
    this.preserveOutOfRangeCategoryFilters(form);
  }

  /**
   * filter.values の 100 件制限により UI に表示されないが URL では絞り込み中の
   * カテゴリーフィルター値を、フォーム送信時に脱落させないため、hidden input として
   * フォームに一時的に補完する。
   *
   * 判定ロジック:
   *  - 「URL に値がある」かつ「同じ value の input がフォーム内に存在しない」値のみ補完
   *  - input 自体が存在する場合はユーザーの UI 操作（チェック解除など）を尊重して補完しない
   *
   * 補完用 input は data-fallback-category-filter 属性を付け、再実行時に重複しないよう除去する。
   */
  preserveOutOfRangeCategoryFilters(form) {
    if (!form) return;
    const CATEGORY_FILTER = 'filter.p.m.custom.category';
    const FALLBACK_ATTR = 'data-fallback-category-filter';

    form.querySelectorAll(`[${FALLBACK_ATTR}]`).forEach((el) => el.remove());

    const currentParams = new URLSearchParams(window.location.search);
    const currentValues = currentParams.getAll(CATEGORY_FILTER).filter(Boolean);
    if (currentValues.length === 0) return;

    const inputValuesInForm = new Set();
    form.querySelectorAll(`[name="${CATEGORY_FILTER}"]`).forEach((input) => {
      if (input.value) inputValuesInForm.add(input.value);
    });

    currentValues.forEach((value) => {
      if (inputValuesInForm.has(value)) return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = CATEGORY_FILTER;
      input.value = value;
      input.setAttribute(FALLBACK_ATTR, 'true');
      form.appendChild(input);
    });
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

// 「絞り込み」ボタンから search-drawer を開く（ファセットUIとは別導線）
if (!window.__facetsSearchDrawerBound) {
  window.__facetsSearchDrawerBound = true;
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('.js-facets__search-open');
    if (!btn) return;
    const drawer = document.querySelector('search-drawer');
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    drawer.open(btn);
  });
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

/**
 * filter.values の 100 件に入らないカテゴリは active_values に出ない。
 * URL の GID を、検索セレクト（search-filters）のカテゴリ名へ引き、足りないピルだけ足す。
 * 100 件以内のピルは Liquid が data-param-value 付きで出しているので触らない。
 * renderActiveFacets が .active-facets-desktop を差し替えるため、その後にも足し直す。
 */
const CATEGORY_PILL_PARAM = 'filter.p.m.custom.category';
let categoryPillLabels = null;
let categoryPillLabelsPromise = null;
let categoryPillSyncToken = 0;

function loadCategoryPillLabels() {
  if (categoryPillLabels) return Promise.resolve(categoryPillLabels);
  if (!categoryPillLabelsPromise) {
    categoryPillLabelsPromise = fetch('/collections/all?sections=search-filters')
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const html = data && data['search-filters'];
        const labels = {};
        if (typeof html === 'string') {
          const wrap = document.createElement('div');
          wrap.innerHTML = html;
          wrap.querySelectorAll('option[data-param-value]').forEach((option) => {
            const gid = option.getAttribute('data-param-value');
            const label = option.textContent.replace(/\s+/g, ' ').trim();
            if (gid && label) labels[gid] = label;
          });
        }
        categoryPillLabels = labels;
        return labels;
      })
      .catch(() => {
        categoryPillLabelsPromise = null;
        return {};
      });
  }
  return categoryPillLabelsPromise;
}

function hrefWithoutCategoryValue(gid) {
  const params = new URLSearchParams(window.location.search);
  const remaining = params.getAll(CATEGORY_PILL_PARAM).filter((value) => value !== gid);
  params.delete(CATEGORY_PILL_PARAM);
  remaining.forEach((value) => params.append(CATEGORY_PILL_PARAM, value));
  const query = params.toString();
  return `${window.location.pathname}${query ? `?${query}` : ''}`;
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function updateCategoryPillMargin(container) {
  const bottom = document.querySelector('.c-facets__bottom');
  const top = document.querySelector('.c-facets__top');
  if (!bottom || !top) return;
  const hasPills = Boolean(container.querySelector('.c-tag'));
  bottom.classList.toggle('has-margin', hasPills && !top.classList.contains('visually-hidden'));
}

function insertOutOfRangeCategoryPills(labels) {
  const container = document.querySelector('#FacetFiltersPillsForm .c-facets__buttons-container');
  if (!container) return;

  container.querySelectorAll('[data-fallback-category-pill]').forEach((el) => el.remove());

  const rendered = new Set(
    Array.from(container.querySelectorAll('[data-param-value]'), (el) => el.getAttribute('data-param-value'))
  );
  const gids = new URLSearchParams(window.location.search).getAll(CATEGORY_PILL_PARAM).filter(Boolean);
  const clearLabel = container.getAttribute('data-clear-filter-label') || '';
  const html = [];

  gids.forEach((gid) => {
    if (rendered.has(gid)) return;
    const label = labels[gid];
    if (!label) return;
    rendered.add(gid);
    html.push(
      `<facet-remove class="c-tag" data-fallback-category-pill data-param-value="${escapeHtml(gid)}"><a href="${escapeHtml(hrefWithoutCategoryValue(gid))}" class="c-tag__link"><span class="c-tag__text">${escapeHtml(label)}</span><span class="c-tag__mark"><span class="c-tag__icon u-icon"><svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L8 8" stroke="#3D3D43" stroke-width="0.9"/><path d="M8 1L1 8" stroke="#3D3D43" stroke-width="0.9"/></svg></span></span>${clearLabel ? `<span class="visually-hidden">${escapeHtml(clearLabel)}</span>` : ''}</a></facet-remove>`
    );
  });

  if (html.length > 0) {
    container.insertAdjacentHTML('afterbegin', html.join(''));
  }

  updateCategoryPillMargin(container);
}

function syncOutOfRangeCategoryPills() {
  const container = document.querySelector('#FacetFiltersPillsForm .c-facets__buttons-container');
  if (!container) return;

  const rendered = new Set(
    Array.from(container.querySelectorAll('[data-param-value]:not([data-fallback-category-pill])'), (el) =>
      el.getAttribute('data-param-value')
    )
  );
  const missing = new URLSearchParams(window.location.search)
    .getAll(CATEGORY_PILL_PARAM)
    .filter((gid) => gid && !rendered.has(gid));

  if (missing.length === 0) {
    container.querySelectorAll('[data-fallback-category-pill]').forEach((el) => el.remove());
    updateCategoryPillMargin(container);
    return;
  }

  const token = ++categoryPillSyncToken;
  loadCategoryPillLabels().then((labels) => {
    if (token !== categoryPillSyncToken) return;
    insertOutOfRangeCategoryPills(labels || {});
  });
}

syncOutOfRangeCategoryPills();
